/**
 * ★ A VOZ CONSOLIDA O TEXTO DA RESPOSTA — [spec-008 T072], D-O-6.
 *
 * Décimo irmão de `transporte-tempo-real.js`, separado de
 * `-remissoes.js` porque aquele arquivo fechou em 448 linhas e o teto do repo
 * é 500. A divisão é a mesma de sempre: lá ficam as remissões CONGELADAS (o
 * que a voz pode dizer quando ela não diz conteúdo) e a guarda que as tranca;
 * aqui fica o desfecho que **leva o texto** e a conferência dele.
 *
 * ## ★ O que foi medido antes de mexer, e por que mexer
 *
 * A voz foi CONSTRUÍDA para não falar o conteúdo, e estava funcionando como
 * escrita. No caso comum de uma consulta de leitura, `_saidaDoDesfecho`
 * (`-delegacao.js`) escolhe:
 *
 * | desfecho do turno | saída | o que o consultor OUVE |
 * |---|---|---|
 * | escrita esperando decisão | `SAIDA_DECISAO_NA_TELA` | "revise e confirme na tela" |
 * | cartão `card` com `format` | `montarSaidaComValor` | o número arredondado por nós |
 * | **qualquer outra coisa com texto** | **`SAIDA_PRONTA`** | **"terminou, está no cartão"** — e nada mais |
 * | nada | `SAIDA_DE_FALHA` | "não consegui agora" |
 *
 * E "qualquer outra coisa" é a **regra**, não a exceção: o único produtor de
 * `valor_para_voz` é o cartão `card` de valor único
 * (`contexto-de-voz-tempo-real.js`), que é **1 dos 19** tipos do
 * `ui-bi-type.schema.json` e sai de **1 das 118** tools do backend
 * (`get_indicador_bi`, via `_bi_projecao.py`). Posição, movimentação, tabela,
 * ranking, série, parecer — tudo isso tem texto na tela e cai em
 * `SAIDA_PRONTA`, cuja `instrucao_de_fala` diz, com todas as letras: *"NÃO
 * diga valores, nomes de cliente, nomes de ativo, datas nem períodos"*.
 *
 * Ou seja: **a voz calada no fim do turno não era defeito, era o desenho.** O
 * owner pediu o oposto, e com estas palavras (17/09/2026): *"sincronizar
 * melhor o texto e a voz: agora já estamos entregando o texto na tela; se
 * passar pela voz com a voz consolidando aquele texto em uma conversa fluida
 * resolve"*.
 *
 * ## ★ O que a D-O-6 NÃO afrouxa
 *
 * | invariante | por que continua de pé |
 * |---|---|
 * | **o CARTÃO é a fonte auditável** | o valor exato, o cliente, o ativo, o período e a data-base seguem na tela, intocados. O G-14 mediu o áudio divergindo do próprio transcript em **2 de 31** sessões (4,2 → 4,4 num dígito de percentual): a fala é conveniência, a tela é prova |
 * | **D-O-3 — quem arredonda somos nós** | quando o turno tem UM valor com tipo, ele viaja em `valor_falado`, saído de `formatarParaFala`, e a instrução manda dizer aquilo e não recalcular do texto |
 * | **nada de número inventado** | o ÚNICO conteúdo que atravessa é o texto que **já está na tela do consultor**. Não há aqui ponto onde um número seja derivado, estimado ou completado — e a guarda refaz a montagem a partir da origem para provar isso |
 * | **N16/N21 — o identificador** | ★ **decisão do owner, 18/09/2026: a voz PODE dizer nome e dados do cliente.** Palavras dele: *"é esperado que a plataforma realmente fale esses dados (até pq o usuario do outro lado vai estar falando)"* — o consultor diz o nome do cliente em voz alta para fazer a pergunta, então o sigilo ambiental nunca dependeu da voz da plataforma. A proibição que FICA é a de **alterar ou abreviar**, que é a deriva medida no G-11 (`NTN-B 2035` → `NTN-B`, 6/11) |
 *
 * ⚠️ **Quem for "consertar" isto de volta, leia a linha acima.** A permissão de
 * falar nome de cliente é escolha consciente, datada e com motivo — não
 * descuido herdado da `SAIDA_PRONTA`. O que continua sendo defeito é a fala
 * DEFORMAR o identificador: valor certo atribuído ao papel errado é pior que
 * valor errado, porque o primeiro parece confiável.
 *
 * ⚠️ **O que nenhum teste daqui cobre, e é preciso dizer:** o que o MODELO faz
 * com o texto. Um invariante sobre a nossa saída não é um invariante sobre o
 * efeito — foi essa confusão que deixou 13 testes verdes aprovarem a espera
 * muda ([T071]). O que se verifica aqui é que **o texto da tela chega ao
 * desfecho, inteiro e sem enxerto**; que ele vire uma fala fiel é medido no
 * fio, pela sonda paga.
 */
import { formatarParaFala } from './formatar-valor-para-voz'
import {
  ErroDeVozTempoReal,
  MOTIVO_SAIDA_RECITA_DADO,
} from './transporte-tempo-real-protocolo'

/**
 * ★ A chave por onde a RESPOSTA DA TELA atravessa para a fala — e a que faz a
 * guarda escolher o regime de conferência, do mesmo jeito que
 * `valor_falado` já fazia.
 */
export const CHAVE_DA_RESPOSTA_NA_TELA = 'resposta_na_tela'

/**
 * ★ O teto do que viaja ao modelo, em caracteres.
 *
 * Uma resposta de chat cabe folgada; o que não cabe é relatório — e relatório
 * se lê na TELA, que é onde ele está inteiro. O teto existe por dois motivos
 * concretos, e nenhum deles é estética: o `function_call_output` é cobrado
 * como contexto do provedor a cada turno, e um texto de vários milhares de
 * caracteres empurra a fala para uma leitura em voz alta em vez de um resumo.
 */
export const LIMITE_DE_TEXTO_CONSOLIDADO = 1500

/**
 * A fração do teto abaixo da qual não vale a pena procurar fronteira de frase:
 * cortar em 40% do texto para terminar num ponto final entrega menos conteúdo
 * do que cortar no meio de uma palavra e avisar que cortou.
 */
const _PISO_DA_FRONTEIRA = 0.6

/**
 * Corta no limite, preferindo a última fronteira de parágrafo ou de frase —
 * um texto que termina no meio de "R$ 1.30" convida o modelo a completar o
 * número, que é precisamente o que não pode acontecer.
 */
const _cortarNoLimite = (texto) => {
  if (texto.length <= LIMITE_DE_TEXTO_CONSOLIDADO) return texto
  const bruto = texto.slice(0, LIMITE_DE_TEXTO_CONSOLIDADO)
  const piso = Math.floor(LIMITE_DE_TEXTO_CONSOLIDADO * _PISO_DA_FRONTEIRA)
  const fimDeParagrafo = bruto.lastIndexOf('\n')
  const fimDeFrase = bruto.lastIndexOf('. ')
  const corte = Math.max(fimDeParagrafo, fimDeFrase)
  if (corte < piso) return bruto.trim()
  return bruto.slice(0, corte + 1).trim()
}

/**
 * ★ As partes da instrução, congeladas e SEPARADAS — a base vale sempre, as
 * outras duas entram só quando o fato delas existe.
 *
 * Elas são concatenadas numa ordem fixa por `_instrucaoDeConsolidar`, e é isso
 * que mantém a montagem determinística: a guarda refaz a mesma concatenação a
 * partir da origem e compara byte a byte. Escrever a instrução à mão no ponto
 * de uso é o que fica vermelho.
 *
 * ⚠️ **Sem nenhum ALGARISMO aqui dentro, de propósito** ("duas ou três", não
 * "2 ou 3"). Há um teste que varre a saída inteira e exige que todo dígito que
 * ela carrega venha do texto da tela ou do formatador — um algarismo de copy
 * furaria a varredura e, pior, poria um número na frente do modelo que não
 * veio do dado.
 */
export const INSTRUCAO_DE_CONSOLIDAR =
  'A consulta terminou. Em `resposta_na_tela` está a resposta INTEIRA, ' +
  'exatamente como o consultor a está lendo no cartão desta conversa. ' +
  'Consolide esse texto numa fala curta e natural, de duas ou três frases, ' +
  'como quem explica o resultado em voz alta para quem está do outro lado. ' +
  'Regras: diga APENAS o que está em `resposta_na_tela` — não acrescente ' +
  'nenhum número, nome, data ou conclusão que não esteja lá, e não complete ' +
  'o que faltar; não leia tabelas nem listas item a item, resuma e remeta ao ' +
  'cartão para o detalhe; não altere nem abrevie nomes de cliente e de ' +
  'ativo, diga-os como estão escritos ou remeta ao cartão; número com muitas ' +
  'casas você NÃO lê inteiro nem arredonda por conta própria — diga que o ' +
  'valor exato está no cartão; o período e a data-base ficam no cartão, que é ' +
  'onde o consultor confere.'

export const INSTRUCAO_DO_VALOR_JA_FORMATADO =
  ' O número principal já está pronto em `valor_falado`: diga-o EXATAMENTE ' +
  'como está, sem arredondar de novo, sem acrescentar casas e sem trocar a ' +
  'escala — e não o recalcule a partir do texto.'

export const INSTRUCAO_DO_TEXTO_CORTADO =
  ' O texto foi cortado por ser longo: diga o que há nele e avise que o ' +
  'restante está no cartão.'

const _instrucaoDeConsolidar = ({ temValor, cortado }) => {
  let instrucao = INSTRUCAO_DE_CONSOLIDAR
  if (temValor) instrucao += INSTRUCAO_DO_VALOR_JA_FORMATADO
  if (cortado) instrucao += INSTRUCAO_DO_TEXTO_CORTADO
  return instrucao
}

/**
 * ★ O desfecho que LEVA o texto — o coração da D-O-6.
 *
 * Quem monta não escolhe o que vai: o texto é o do turno, cru (só `trim` e o
 * corte determinístico acima), e o número é o que `formatarParaFala`
 * produziu. Não existe aqui caminho que escreva conteúdo à mão, e é isso que
 * faz `conferirAConsolidacao` passar sempre no caminho feliz e falhar sempre
 * num enxerto.
 *
 * ⚠️ **`null` quando não há texto** — e a ausência é resultado, não erro: sem
 * resposta na tela não há o que consolidar, e quem chama volta aos caminhos
 * anteriores (o valor sozinho, a remissão, a falha). É a mesma régua de
 * `formatarParaFala`, que devolve `''` quando não há número legível: uma só
 * função decide se o material existe.
 *
 * @param {Object} opts
 * @param {string} opts.texto a resposta do assistente que foi para a TELA.
 * @param {string} [opts.valorFalado] a saída de `formatarParaFala`, quando o
 *   turno teve UM valor com tipo declarado. Vazio ⇒ a chave não entra.
 * @returns {?Object} a saída congelada, ou `null` se não há texto.
 */
export const montarSaidaDeConsolidacao = ({ texto, valorFalado } = {}) => {
  const limpo = String(texto || '').trim()
  if (!limpo) return null
  const cortado = _cortarNoLimite(limpo)
  const foiCortado = cortado.length < limpo.length
  const numero = String(valorFalado || '')
  // A ordem das chaves é FIXA porque a guarda compara a serialização inteira.
  const saida = {
    status: 'pronto',
    onde: 'cartao_da_conversa',
    resposta_na_tela: cortado,
  }
  if (foiCortado) saida.resposta_na_tela_cortada = true
  if (numero) saida.valor_falado = numero
  saida.instrucao_de_fala = _instrucaoDeConsolidar({
    temValor: Boolean(numero),
    cortado: foiCortado,
  })
  return Object.freeze(saida)
}

/**
 * ★ O invariante da D-O-6, em código:
 *
 * > **O que a voz consolida é exatamente o texto que foi para a tela neste
 * > turno — nem outro, nem enxertado, nem completado.**
 *
 * A verificação é a mesma técnica da D-O-3, e de propósito: refaz a montagem a
 * partir da ORIGEM (o desfecho do turno) e exige que a saída inteira seja
 * idêntica. Uma comparação cobre as cinco maneiras de violar o contrato —
 * mandar outro texto, acrescentar uma frase de conclusão, enxertar um campo,
 * reescrever a `instrucao_de_fala` e pôr um número que o formatador não
 * produziu.
 *
 * ⚠️ `origem` ausente com `resposta_na_tela` presente é VERMELHO, e este é o
 * caso que mais importa: um texto na fala sem origem contra o que conferir é
 * precisamente o texto que veio de lugar nenhum.
 *
 * @param {Object} saida a que vai ao `function_call_output`
 * @param {Object} [origem] `{texto, valor, tipo}` — o que o TURNO devolveu.
 * @throws {ErroDeVozTempoReal} `MOTIVO_SAIDA_RECITA_DADO`
 */
export const conferirAConsolidacao = (saida, origem) => {
  const pedido = origem || {}
  const esperada = montarSaidaDeConsolidacao({
    texto: pedido.texto,
    valorFalado: formatarParaFala(pedido.valor, { tipo: pedido.tipo }),
  })
  if (esperada && JSON.stringify(saida) === JSON.stringify(esperada)) return
  throw new ErroDeVozTempoReal(
    MOTIVO_SAIDA_RECITA_DADO,
    'A consolidação não é o texto que foi para a tela neste turno: o que a ' +
      'voz resume é a resposta do cartão, e quem a monta é o desfecho.'
  )
}

// ── [T073] A MESMA consolidação, pelo outro caminho: a fala empurrada ─────

/**
 * ★ O conteúdo do `session.commentary.append` que entrega a resposta quando o
 * laço da delegação já fechou (o "dois tempos" da [T073]).
 *
 * É **o mesmo texto** que a saída da D-O-6 leva em `resposta_na_tela` — a
 * resposta da tela, cortada na fronteira de frase. Não há uma segunda régua de
 * corte nem uma segunda redação: se existissem, a fala e o cartão passariam a
 * divergir no primeiro ajuste de qualquer uma das duas.
 *
 * ⚠️ **Por que a fala não leva a `instrucao_de_fala`:** ali o conteúdo do
 * append é o que o modelo consolida em voz alta. Medido em 17/09 (sondagem S3,
 * sessão ociosa): conteúdo DECLARATIVO já faz o modelo falar, com as palavras
 * dele — mandar a instrução junto poria a instrução na boca dele.
 *
 * @returns {string} o texto a empurrar, ou `''` quando não há o que consolidar.
 */
export const PREFIXO_DA_FALA_DE_DESFECHO =
  'Responda agora ao consultor, em voz alta e com as suas palavras, a partir ' +
  'deste resultado — sem acrescentar nenhum dado que não esteja nele: '

/**
 * ⚠️ A cláusula do número existe por um defeito MEDIDO no v1: mandado o valor
 * exato, o modelo arredondou em 2 de 4 execuções e não arredondou nas outras 2.
 * Quem arredonda somos nós (D-O-3) — e o texto da tela carrega o valor EXATO,
 * então sem esta cláusula a fala voltaria a depender do humor dele.
 *
 * ★ O teor é o MESMO de `INSTRUCAO_DO_VALOR_JA_FORMATADO`, que era o que o
 * `function_call_output` levava: "diga EXATAMENTE como está, sem arredondar de
 * novo, sem acrescentar casas, sem trocar a escala, e não recalcule a partir
 * do texto". Mudar de canal não podia custar a orientação — e ia custar: a
 * primeira versão desta cláusula dizia só "não arredonde de novo", e foi um
 * teste da suíte antiga que apontou a perda.
 */
export const clausulaDoNumeroFalado = (valorFalado) =>
  ' O número principal já está pronto: diga-o EXATAMENTE como está — "' +
  String(valorFalado) +
  '" —, sem arredondar de novo, sem acrescentar casas e sem trocar a escala, ' +
  'e não o recalcule a partir do texto.'

export const montarFalaDeConsolidacao = ({ texto, valorFalado } = {}) => {
  const saida = montarSaidaDeConsolidacao({ texto, valorFalado })
  if (!saida) return ''
  const numero = String(valorFalado || '')
  return (
    PREFIXO_DA_FALA_DE_DESFECHO +
    saida[CHAVE_DA_RESPOSTA_NA_TELA] +
    (numero ? clausulaDoNumeroFalado(numero) : '')
  )
}

/**
 * ★ O MESMO invariante da `conferirAConsolidacao`, no canal da fala: o que a
 * voz recebe é exatamente o texto que foi para a tela neste turno.
 *
 * Refaz a montagem a partir da ORIGEM e compara — a técnica da D-O-3/D-O-6.
 * Sem origem é VERMELHO pela mesma razão de lá: texto na fala sem nada contra
 * o que conferir é precisamente o texto que veio de lugar nenhum.
 *
 * @param {string} conteudo o que iria no `content` do append
 * @param {Object} [origem] `{texto, valor, tipo}` — o desfecho do turno
 * @throws {ErroDeVozTempoReal} `MOTIVO_SAIDA_RECITA_DADO`
 */
export const conferirAFalaDeConsolidacao = (conteudo, origem) => {
  const pedido = origem || {}
  const esperado = montarFalaDeConsolidacao({
    texto: pedido.texto,
    valorFalado: formatarParaFala(pedido.valor, { tipo: pedido.tipo }),
  })
  if (esperado && String(conteudo) === esperado) return
  throw new ErroDeVozTempoReal(
    MOTIVO_SAIDA_RECITA_DADO,
    'A fala empurrada não é o texto que foi para a tela neste turno: o que a ' +
      'voz consolida é a resposta do cartão, e quem a monta é o desfecho.'
  )
}
